/**
 * Admin oversight of EIR outreach Connections — read-only.
 *
 * Modeled on the founder Investor CRM table (InvestorCRM.js): searchable /
 * filterable table with a slide-panel detail + notes thread. Unlike the EIR's
 * own Connections page, this is program-wide and view-only: it adds an "Owner"
 * (EIR) column and an EIR filter, and exposes no create/edit/invite/delete.
 * Backed by GET /connections (admins receive all; ?eir_id / ?status filter).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { listConnections, getEIRList, getConnectionNotes } from '../services/apiService';
import SlidePanel from '../components/SlidePanel';

const STATUSES = [
  { value: 'met',          label: 'Met',          color: '#6b7280', bg: '#f3f4f6' },
  { value: 'in_touch',     label: 'In Touch',     color: '#2563eb', bg: '#eff6ff' },
  { value: 'invited',      label: 'Invited',      color: '#d97706', bg: '#fef3c7' },
  { value: 'applied',      label: 'Applied',      color: '#7c3aed', bg: '#f5f3ff' },
  { value: 'onboarded',    label: 'Onboarded',    color: '#15803d', bg: '#dcfce7' },
  { value: 'not_pursuing', label: 'Not Pursuing', color: '#dc2626', bg: '#fee2e2' },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.met;
  return <span style={{ ...styles.statusPill, background: s.bg, color: s.color }}>{s.label}</span>;
}

/** Program-wide, read-only view of every EIR's connections. */
export default function AdminConnections() {
  const [connections, setConnections] = useState([]);
  const [eirs, setEirs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eirFilter, setEirFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    getEIRList().then(setEirs).catch(() => setEirs([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (eirFilter) params.eir_id = eirFilter;
    if (statusFilter) params.status = statusFilter;
    listConnections(params)
      .then(({ connections: c }) => setConnections(c))
      .finally(() => setLoading(false));
  }, [eirFilter, statusFilter]);

  const openPanel = useCallback((conn) => {
    setSelected(conn);
    setPanelOpen(true);
  }, []);

  const filtered = connections.filter((c) => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q)
      || (c.company || '').toLowerCase().includes(q)
      || (c.eir_name || '').toLowerCase().includes(q);
  });

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Connections</h1>
            <p style={styles.subtitle}>Outreach connections across all EIRs (read-only).</p>
          </div>
        </div>

        <div style={styles.controls}>
          <div style={styles.searchWrap}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={styles.searchIcon}>
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input style={styles.searchInput} placeholder="Search name, company, or EIR…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select style={styles.filterSelect} value={eirFilter} onChange={(e) => setEirFilter(e.target.value)}>
            <option value="">All EIRs</option>
            {eirs.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
          </select>
          <select style={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={{ ...styles.th, width: 36 }}>#</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Name</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Company</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Connection</th>
                <th style={{ ...styles.th, minWidth: 150 }}>Where met</th>
                <th style={{ ...styles.th, minWidth: 120 }}>Status</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Next follow-up</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={styles.emptyCell}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} style={styles.emptyCell}>No connections found.</td></tr>
              )}
              {filtered.map((c, i) => (
                <tr key={c.id} style={styles.tr} onClick={() => openPanel(c)}>
                  <td style={styles.numCell}>{i + 1}</td>
                  <td style={{ ...styles.td, fontWeight: 700 }}>{c.name}</td>
                  <td style={styles.td}>{c.company || <span style={styles.dim}>—</span>}</td>
                  <td style={styles.td}>{c.eir_name || <span style={styles.dim}>—</span>}</td>
                  <td style={styles.td}>{c.source || <span style={styles.dim}>—</span>}</td>
                  <td style={styles.td}><StatusPill status={c.status} /></td>
                  <td style={styles.td}>{c.next_follow_up_date || <span style={styles.dim}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={selected?.name || ''}>
        {selected && <ConnectionDetail conn={selected} />}
      </SlidePanel>
    </div>
  );
}

// Read-only detail: connection fields + notes thread (no editing for admins).
function ConnectionDetail({ conn }) {
  const [notes, setNotes] = useState([]);
  useEffect(() => { getConnectionNotes(conn.id).then(setNotes).catch(() => setNotes([])); }, [conn.id]);

  const rows = [
    ['Connection', conn.eir_name],
    ['Company', conn.company],
    ['Email', conn.email],
    ['Phone', conn.phone],
    ['Title', conn.title],
    ['Where met', conn.source],
    ['Next step', conn.next_step],
    ['Next follow-up', conn.next_follow_up_date],
    ['Invited', conn.invited_at ? new Date(conn.invited_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null],
  ];

  return (
    <div style={panelStyles.wrap}>
      <StatusPill status={conn.status} />

      <div style={panelStyles.rows}>
        {rows.map(([label, value]) => (
          <div key={label} style={panelStyles.row}>
            <span style={panelStyles.label}>{label}</span>
            <span style={panelStyles.value}>{value || <span style={styles.dim}>—</span>}</span>
          </div>
        ))}
      </div>

      <div style={panelStyles.divider} />

      <p style={panelStyles.notesTitle}>
        Notes {notes.length > 0 && <span style={panelStyles.noteCount}>{notes.length}</span>}
      </p>
      <div style={panelStyles.notesList}>
        {notes.length === 0 && <p style={panelStyles.dimText}>No notes.</p>}
        {[...notes].reverse().map((n) => (
          <div key={n.id} style={panelStyles.noteItem}>
            <p style={panelStyles.noteBody}>{n.body}</p>
            <p style={panelStyles.noteDate}>{n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: '98%', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 'var(--space-5)', paddingTop: 'var(--space-2)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 'var(--font-size-page-title)', fontWeight: 'var(--font-weight-page-title)', color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 4 },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-page-subtitle)', color: 'var(--color-forest)', opacity: 0.7 },
  controls: { display: 'flex', gap: 12, marginBottom: 'var(--space-3)', flexWrap: 'wrap' },
  searchWrap: { flex: 1, minWidth: 200, position: 'relative' },
  searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-forest)', opacity: 0.4 },
  searchInput: { width: '100%', padding: '8px 12px 8px 34px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 20, color: 'var(--color-forest)', boxSizing: 'border-box', outline: 'none' },
  filterSelect: { padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none' },
  tableWrap: { background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', overflow: 'auto', marginBottom: 8 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 760 },
  thead: { background: '#f8fafb' },
  th: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  td: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', padding: '10px 12px', verticalAlign: 'middle' },
  numCell: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.35, padding: '10px 12px', textAlign: 'center' },
  emptyCell: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.45, padding: '28px 16px', textAlign: 'center', fontStyle: 'italic' },
  dim: { opacity: 0.35 },
  statusPill: { padding: '3px 12px', borderRadius: 'var(--radius-full)', fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-body)', display: 'inline-block' },
};

const panelStyles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  rows: { display: 'flex', flexDirection: 'column', gap: 2 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, borderBottom: '1px solid #f0f0f0', padding: '8px 0' },
  label: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 800, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' },
  value: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', textAlign: 'right' },
  divider: { height: 1, background: '#f0f0f0' },
  notesTitle: { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--color-forest)', display: 'flex', alignItems: 'center', gap: 8 },
  noteCount: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, background: 'var(--color-mint-dark)', color: 'var(--color-forest)', padding: '1px 7px', borderRadius: 'var(--radius-full)' },
  notesList: { display: 'flex', flexDirection: 'column', gap: 8 },
  noteItem: { background: '#f8fafb', borderRadius: 'var(--radius-sm)', padding: '10px 12px', borderLeft: '3px solid var(--color-mint-dark)' },
  noteBody: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  noteDate: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.45, marginTop: 4 },
  dimText: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.4, fontStyle: 'italic' },
};
